import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { getUserInfoSync } from '../../config/api'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

const numberFormatter = new Intl.NumberFormat('en-US')

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A'
  }

  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const DetailRow = ({ label, value }) => (
  <div className="mb-3">
    <small className="text-muted text-uppercase fw-semibold d-block">{label}</small>
    <div className="fw-semibold text-dark">{value ?? 'N/A'}</div>
  </div>
)

const getPreferredWarehouseId = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.warehouse === 'number') {
    return userInfo.warehouse
  }

  if (typeof userInfo.warehouse === 'object' && userInfo.warehouse !== null) {
    if (typeof userInfo.warehouse.id === 'number') {
      return userInfo.warehouse.id
    }
  }

  return (
    userInfo.warehouse_id ||
    userInfo.assigned_warehouse_id ||
    userInfo.warehouseId ||
    null
  )
}

const ViewStock = () => {
  const userInfo = getUserInfoSync()
  const preferredWarehouseId = useMemo(() => getPreferredWarehouseId(userInfo), [userInfo])

  const [stockEntries, setStockEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [stockValueSummary, setStockValueSummary] = useState(null)
  const [stockValueLoading, setStockValueLoading] = useState(true)

  const fetchStockEntries = useCallback(async () => {
    if (!preferredWarehouseId) {
      setErrorMessage('No warehouse is linked to your profile. Please contact your administrator.')
      setStockEntries([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setErrorMessage('')

      const response = await apiClient.get('/api/stock/entries/', {
        params: preferredWarehouseId ? { warehouse_id: preferredWarehouseId } : undefined
      })
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setStockEntries(data)
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Error fetching stock entries:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to load stock entries. Please try again.'
      )
      setStockEntries([])
    } finally {
      setLoading(false)
    }
  }, [preferredWarehouseId])

  const fetchStockValueSummary = useCallback(async () => {
    if (!preferredWarehouseId) {
      setStockValueSummary(null)
      setStockValueLoading(false)
      return
    }

    try {
      setStockValueLoading(true)
      const response = await apiClient.get('/api/stock/value/', {
        params: { warehouse_id: preferredWarehouseId }
      })
      setStockValueSummary(response.data || null)
    } catch (error) {
      console.error('Error fetching stock value summary:', error)
    } finally {
      setStockValueLoading(false)
    }
  }, [preferredWarehouseId])

  useEffect(() => {
    if (!preferredWarehouseId) {
      setStockValueSummary(null)
      setStockEntries([])
      setLoading(false)
      setStockValueLoading(false)
      setErrorMessage('No warehouse is linked to your profile. Please contact your administrator.')
      return
    }

    fetchStockEntries()
    fetchStockValueSummary()
  }, [fetchStockEntries, fetchStockValueSummary, preferredWarehouseId])

  const filteredEntries = useMemo(() => {
    if (!searchTerm) {
      return stockEntries
    }

    const query = searchTerm.toLowerCase()
    return stockEntries.filter(
      (entry) =>
        entry.product_name?.toLowerCase().includes(query) ||
        entry.product_sku?.toLowerCase().includes(query) ||
        entry.supplier_name?.toLowerCase().includes(query)
    )
  }, [stockEntries, searchTerm])

  const renderDetailView = () => {
    if (!selectedEntry) {
      return null
    }

    return (
      <section className="section">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <button
                  type="button"
                  className="btn btn-link px-0 mb-3 text-decoration-none"
                  onClick={() => setSelectedEntry(null)}
                >
                  <i className="bi bi-arrow-left me-1" />
                  Back to stock list
                </button>
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                  <div>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: 58,
                          height: 58,
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, #2b5876 0%, #4e4376 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '24px',
                          boxShadow: '0 4px 12px rgba(46, 49, 146, 0.3)'
                        }}
                      >
                        <i className="bi bi-box" />
                      </div>
                      <div>
                        <h4 className="mb-1">{selectedEntry.product_name || 'Stock Entry'}</h4>
                        <small className="text-muted d-block">
                          SKU {selectedEntry.product_sku || 'N/A'}
                          {' • '}
                          Batch {selectedEntry.batch_number || 'N/A'}
                        </small>
                        <small className="text-muted">
                          Supplier: {selectedEntry.supplier_name || 'Unknown'}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="text-lg-end">
                    <div className="badge bg-primary bg-opacity-10 text-primary fw-semibold mb-2">
                      Entry #{selectedEntry.id}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge bg-light text-dark border">
                        Warehouse: {selectedEntry.warehouse_name || 'N/A'}
                      </span>
                      <span className="badge bg-light text-dark border">
                        Supplier: {selectedEntry.supplier_name || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <hr />

                <div className="row g-3 mb-4">
                  <div className="col-md-4 col-12">
                    <div
                      className="d-flex align-items-center justify-content-between w-100"
                      style={{
                        background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                        padding: '16px 18px',
                        borderRadius: '16px',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(33, 147, 176, 0.35)'
                      }}
                    >
                      <div>
                        <div className="text-uppercase small fw-semibold">Quantity</div>
                        <h4 className="mb-0">
                          {numberFormatter.format(Number(selectedEntry.quantity) || 0)}
                        </h4>
                      </div>
                      <i className="bi bi-stack fs-2 opacity-50" />
                    </div>
                  </div>
                  <div className="col-md-4 col-12">
                    <div
                      className="d-flex align-items-center justify-content-between w-100"
                      style={{
                        background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                        padding: '16px 18px',
                        borderRadius: '16px',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(247, 151, 30, 0.35)'
                      }}
                    >
                      <div>
                        <div className="text-uppercase small fw-semibold">Purchase Price</div>
                        <h4 className="mb-0">
                          {currencyFormatter.format(Number(selectedEntry.purchase_price) || 0)}
                        </h4>
                      </div>
                      <i className="bi bi-currency-dollar fs-2 opacity-50" />
                    </div>
                  </div>
                  <div className="col-md-4 col-12">
                    <div
                      className="d-flex align-items-center justify-content-between w-100"
                      style={{
                        background: 'linear-gradient(135deg, #b06ab3 0%, #4568dc 100%)',
                        padding: '16px 18px',
                        borderRadius: '16px',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(69, 104, 220, 0.35)'
                      }}
                    >
                      <div>
                        <div className="text-uppercase small fw-semibold">Total Cost</div>
                        <h4 className="mb-0">
                          {currencyFormatter.format(Number(selectedEntry.total_cost) || 0)}
                        </h4>
                      </div>
                      <i className="bi bi-cash-coin fs-2 opacity-50" />
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-lg-6">
                    <div className="border rounded-4 p-4 h-100">
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">Inventory Details</h6>
                      <div className="row">
                        <div className="col-sm-6">
                          <DetailRow label="Warehouse" value={selectedEntry.warehouse_name} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Supplier" value={selectedEntry.supplier_name} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Reorder Level" value={selectedEntry.reorder_level ?? 'N/A'} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Initial Stock" value={selectedEntry.is_initial_stock ? 'Yes' : 'No'} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="border rounded-4 p-4 h-100">
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">Batch & Transfer</h6>
                      <div className="row">
                        <div className="col-sm-6">
                          <DetailRow label="Batch Number" value={selectedEntry.batch_number} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Original Batch" value={selectedEntry.original_batch_number} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Entry Group" value={selectedEntry.entry_group || 'N/A'} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Source Transfer" value={selectedEntry.source_transfer_reference || 'N/A'} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-1">
                  <div className="col-lg-6">
                    <div className="border rounded-4 p-4 h-100">
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">Timeline</h6>
                      <div className="row">
                        <div className="col-sm-6">
                          <DetailRow label="Received Date" value={formatDateTime(selectedEntry.received_date)} />
                        </div>
                        <div className="col-sm-6">
                          <DetailRow label="Created At" value={formatDateTime(selectedEntry.created_at)} />
                        </div>
                        <div className="col-12">
                          <DetailRow label="Created By" value={selectedEntry.created_by_email} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="border rounded-4 p-4 h-100">
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">Notes</h6>
                      <div
                        style={{
                          minHeight: 120,
                          backgroundColor: '#f8f9fb',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px dashed #dfe3eb',
                          color: '#4a4a4a'
                        }}
                      >
                        {selectedEntry.notes || 'No notes captured for this entry.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>View Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/warehouse-manager">Home</a>
            </li>
            <li className="breadcrumb-item">Stock Management</li>
            <li className="breadcrumb-item active">View Stock</li>
          </ol>
        </nav>
      </div>

      {selectedEntry ? (
        renderDetailView()
      ) : (
        <section className="section">
          <div className="row">
            <div className="col-12">
              {errorMessage && (
                <div className="alert alert-danger" role="alert">
                  {errorMessage}
                </div>
              )}

              <div
                className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                style={{
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef',
                  gap: '12px'
                }}
              >
                <div>
                  <h5 className="card-title mb-1">
                    <i className="bi bi-box-seam me-2" />
                    Warehouse Snapshot
                  </h5>
                  <small className="text-muted">Live totals for your warehouse</small>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div
                    className="d-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    <i className="bi bi-stack me-2" />
                    <div>
                      <div>Total Quantity</div>
                      <small style={{ fontWeight: 400 }}>
                        {stockValueLoading
                          ? '---'
                          : `${numberFormatter.format(
                              Number(stockValueSummary?.total_quantity) || 0
                            )} units`}
                      </small>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                    }}
                  >
                    <i className="bi bi-cash-stack me-2" />
                    <div>
                      <div>Total Value</div>
                      <small style={{ fontWeight: 400 }}>
                        {stockValueLoading
                          ? '---'
                          : currencyFormatter.format(Number(stockValueSummary?.total_value) || 0)}
                      </small>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                    }}
                  >
                    <i className="bi bi-currency-dollar me-2" />
                    <div>
                      <div>Avg Purchase Price</div>
                      <small style={{ fontWeight: 400 }}>
                        {stockValueLoading
                          ? '---'
                          : currencyFormatter.format(
                              Number(stockValueSummary?.average_purchase_price) || 0
                            )}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                    <div>
                      <h5 className="card-title mb-0">Current Stock</h5>
                      {lastUpdated && (
                        <small className="text-muted">
                          Last updated {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}
                        </small>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search by product, SKU or supplier"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        style={{ maxWidth: 260 }}
                      />
                      <button type="button" className="btn btn-outline-primary" onClick={fetchStockEntries}>
                        <i className="bi bi-arrow-repeat me-1" />
                        Refresh
                      </button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="py-5 text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : filteredEntries.length ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead>
                          <tr>
                            <th scope="col">Product</th>
                            <th scope="col" className="text-end">
                              Qnty
                            </th>
                            <th scope="col" className="text-end">
                              Purchase Price
                            </th>
                            <th scope="col" className="text-end">
                              Total Cost
                            </th>
                            <th scope="col">Supplier Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEntries.map((entry) => (
                            <tr
                              key={entry.id}
                              role="button"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedEntry(entry)}
                            >
                              <td>
                                <div className="fw-semibold text-dark">{entry.product_name || 'Unnamed Product'}</div>
                                <small className="text-muted">SKU {entry.product_sku || 'N/A'}</small>
                              </td>
                              <td className="text-end fw-semibold">
                                {numberFormatter.format(Number(entry.quantity) || 0)}
                              </td>
                              <td className="text-end">{currencyFormatter.format(Number(entry.purchase_price) || 0)}</td>
                              <td className="text-end">{currencyFormatter.format(Number(entry.total_cost) || 0)}</td>
                              <td>{entry.supplier_name || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-5 text-center text-muted">
                      <i className="bi bi-boxes fs-1 d-block mb-3" />
                      <p className="mb-0">No stock entries available for your warehouse.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default ViewStock

